import React, { useState, useMemo, useEffect } from 'react';
import { Meal, Symptom } from '../lib/types';
import { getActiveTrackableSymptoms, TrackableSymptom } from '../lib/symptomCategories';
import { CustomizeSymptomsModal } from './CustomizeSymptomsModal';
import {
  computeSymptomSpecificTriggers,
  computeLineOfBestFit,
  getSymptomScore,
  extractFoodsFromMeal,
} from '../lib/correlationEngine';
import { formatTime, formatIsoDate } from '../lib/dateUtils';
import { triggerHaptic } from '../lib/haptics';
import {
  ChevronLeft,
  Settings,
  TrendingDown,
  TrendingUp,
  Activity,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Calendar,
  X,
  Sliders,
} from 'lucide-react';

export type AnalyticsTimeframe = '1day' | '3days' | 'week' | 'month';

interface SymptomAnalyticsViewProps {
  meals: Meal[];
  symptoms: Symptom[];
  initialSymptomId?: string;
  onBack: () => void;
  onEditMeal?: (meal: Meal) => void;
  onEditSymptom?: (symptom: Symptom) => void;
}

export const SymptomAnalyticsView: React.FC<SymptomAnalyticsViewProps> = ({
  meals,
  symptoms,
  initialSymptomId = 'bloating',
  onBack,
  onEditMeal,
  onEditSymptom,
}) => {
  const [activeSymptoms, setActiveSymptoms] = useState<TrackableSymptom[]>(() => getActiveTrackableSymptoms());
  const [selectedSymptomId, setSelectedSymptomId] = useState<string>(() => {
    const list = getActiveTrackableSymptoms();
    return list.some((s) => s.id === initialSymptomId) ? initialSymptomId : list[0]?.id || 'bloating';
  });
  const [isCustomizeSymptomsOpen, setIsCustomizeSymptomsOpen] = useState<boolean>(false);
  const [timeframe, setTimeframe] = useState<AnalyticsTimeframe>('week');
  const [timeframeOffset, setTimeframeOffset] = useState<number>(0);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const [showDataPoints, setShowDataPoints] = useState<boolean>(true);
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);
  const [expandedIngredient, setExpandedIngredient] = useState<string | null>(null);

  useEffect(() => {
    const handleUpdate = () => {
      const updated = getActiveTrackableSymptoms();
      setActiveSymptoms(updated);
      if (!updated.some((s) => s.id === selectedSymptomId) && updated.length > 0) {
        setSelectedSymptomId(updated[0].id);
      }
    };
    window.addEventListener('digestlog_custom_symptoms_updated', handleUpdate);
    return () => window.removeEventListener('digestlog_custom_symptoms_updated', handleUpdate);
  }, [selectedSymptomId]);

  const activeSymptomMeta = useMemo(() => {
    return (
      activeSymptoms.find((s) => s.id === selectedSymptomId) || {
        id: selectedSymptomId,
        name: selectedSymptomId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        shortName: selectedSymptomId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        unit: '/10',
        description: '',
      }
    );
  }, [selectedSymptomId, activeSymptoms]);

  // Calculate start & end timestamps for timeframe
  const { periodStartMs, periodEndMs, periodLabel } = useMemo(() => {
    const now = new Date();
    let durationMs = 7 * 24 * 60 * 60 * 1000;

    if (timeframe === '1day') {
      durationMs = 1 * 24 * 60 * 60 * 1000;
    } else if (timeframe === '3days') {
      durationMs = 3 * 24 * 60 * 60 * 1000;
    } else if (timeframe === 'week') {
      durationMs = 7 * 24 * 60 * 60 * 1000;
    } else if (timeframe === 'month') {
      durationMs = 30 * 24 * 60 * 60 * 1000;
    }

    const endMs = now.getTime() - timeframeOffset * durationMs;
    const startMs = endMs - durationMs;

    const startDate = new Date(startMs);
    const endDate = new Date(endMs);

    let label = '';
    if (timeframe === '1day') {
      label = startDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    } else {
      label = `${startDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${endDate.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: startDate.getFullYear() !== endDate.getFullYear() ? 'numeric' : undefined,
      })}`;
    }

    return { periodStartMs: startMs, periodEndMs: endMs, periodLabel: label };
  }, [timeframe, timeframeOffset]);

  // Filter symptoms for active period
  const periodSymptoms = useMemo(() => {
    return symptoms
      .filter((s) => {
        const t = new Date(s.timestamp_iso).getTime();
        return !isNaN(t) && t >= periodStartMs && t <= periodEndMs;
      })
      .sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());
  }, [symptoms, periodStartMs, periodEndMs]);

  // Extract points on 0-10 scale
  const chartPoints = useMemo(() => {
    if (periodSymptoms.length === 0) return [];

    return periodSymptoms.map((s) => {
      const timeMs = new Date(s.timestamp_iso).getTime();
      const val = getSymptomScore(s, selectedSymptomId);

      return {
        symptom: s,
        timeMs,
        val,
        dateLabel: formatIsoDate(new Date(timeMs)),
        timeLabel: formatTime(s.timestamp_iso),
      };
    });
  }, [periodSymptoms, selectedSymptomId]);

  // Suspected ingredient triggers
  const suspectedTriggers = useMemo(() => {
    return computeSymptomSpecificTriggers(meals, symptoms, selectedSymptomId, periodStartMs);
  }, [meals, symptoms, selectedSymptomId, periodStartMs]);

  // Chart layout dimensions (0-10 scale)
  const chartWidth = 340;
  const chartHeight = 160;
  const paddingX = 24;
  const paddingTop = 20;
  const paddingBottom = 28;
  const plotWidth = chartWidth - paddingX * 2;
  const plotHeight = chartHeight - paddingTop - paddingBottom;
  const maxY = 10;
  const minY = 0;

  const regressionInput = useMemo(() => {
    return chartPoints.map((pt) => {
      const progress =
        periodEndMs > periodStartMs
          ? Math.max(0, Math.min(1, (pt.timeMs - periodStartMs) / (periodEndMs - periodStartMs)))
          : 0.5;
      return { progress, val: pt.val };
    });
  }, [chartPoints, periodStartMs, periodEndMs]);

  const bestFit = useMemo(() => {
    return computeLineOfBestFit(regressionInput);
  }, [regressionInput]);

  const bestFitLine = useMemo(() => {
    const x1 = paddingX;
    const y1 = paddingTop + plotHeight - (bestFit.scoreStart / maxY) * plotHeight;
    const x2 = paddingX + plotWidth;
    const y2 = paddingTop + plotHeight - (bestFit.scoreEnd / maxY) * plotHeight;
    return { x1, y1, x2, y2 };
  }, [bestFit, paddingX, plotWidth, paddingTop, plotHeight, maxY]);

  const svgCoordinates = useMemo(() => {
    if (chartPoints.length === 0) return [];
    return chartPoints.map((pt) => {
      const progress =
        periodEndMs > periodStartMs
          ? Math.max(0, Math.min(1, (pt.timeMs - periodStartMs) / (periodEndMs - periodStartMs)))
          : 0.5;
      const x = paddingX + progress * plotWidth;
      const y = paddingTop + plotHeight - (pt.val / maxY) * plotHeight;
      return { x, y, pt };
    });
  }, [chartPoints, periodStartMs, periodEndMs, plotWidth, plotHeight, paddingX, paddingTop, maxY]);

  // Summary statistics on 0-10 scale
  const stats = useMemo(() => {
    if (chartPoints.length === 0) {
      return { avg: '0.0 / 10', peak: '0 / 10', count: 0, positiveCount: 0, trend: 'stable' };
    }
    const values = chartPoints.map((p) => p.val);
    const avgVal = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
    const peakVal = Math.max(...values);
    const positiveCount = values.filter((v) => v > 0).length;

    return {
      avg: `${avgVal} / 10`,
      peak: `${peakVal} / 10`,
      count: chartPoints.length,
      positiveCount,
      trend: bestFit.trend,
    };
  }, [chartPoints, bestFit]);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-black text-slate-900 dark:text-slate-100 pb-28 animate-fade-in">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-30 bg-white/90 dark:bg-[#0c0e14]/90 backdrop-blur-md border-b border-slate-200/80 dark:border-zinc-800 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              onBack();
            }}
            className="flex items-center space-x-1 text-xs font-bold text-slate-700 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white px-2.5 py-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800/80 transition-all active:scale-95 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Insights</span>
          </button>

          {/* Symptom Title Dropdown */}
          <div className="relative">
            <select
              value={selectedSymptomId}
              onChange={(e) => {
                triggerHaptic('light');
                setSelectedSymptomId(e.target.value);
              }}
              className="appearance-none text-xs font-black bg-slate-100 dark:bg-zinc-800/80 text-slate-900 dark:text-white pl-3 pr-7 py-1.5 rounded-xl border border-slate-200 dark:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
            >
              {activeSymptoms.map((sym) => (
                <option key={sym.id} value={sym.id}>
                  {sym.name}
                </option>
              ))}
            </select>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
          </div>

          {/* Settings / Config Button */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              setShowConfigModal(true);
            }}
            className="p-2 rounded-xl text-slate-600 dark:text-zinc-400 hover:text-slate-950 dark:hover:text-white bg-slate-100 dark:bg-zinc-800/80 transition-all active:scale-95 cursor-pointer"
            title="Chart Options"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 space-y-4">
        {/* Timeframe Filter Bar */}
        <div className="flex items-center justify-between bg-white dark:bg-zinc-900 rounded-2xl p-1.5 border border-slate-200/80 dark:border-zinc-800 shadow-2xs">
          {(['1day', '3days', 'week', 'month'] as AnalyticsTimeframe[]).map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setTimeframe(tf);
                setTimeframeOffset(0);
              }}
              className={`flex-1 py-1.5 text-[11px] font-bold rounded-xl transition-all cursor-pointer ${
                timeframe === tf
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black font-black shadow-xs'
                  : 'text-slate-600 dark:text-zinc-400 hover:text-slate-950 dark:hover:text-white'
              }`}
            >
              {tf === '1day' ? '1 Day' : tf === '3days' ? '3 Days' : tf === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between px-1 text-xs">
          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              setTimeframeOffset((prev) => prev + 1);
            }}
            className="p-1.5 rounded-lg bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-50 active:scale-95 cursor-pointer"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>

          <span className="font-bold text-slate-700 dark:text-zinc-300 flex items-center space-x-1.5">
            <Calendar className="w-3.5 h-3.5 text-blue-500" />
            <span>{periodLabel}</span>
          </span>

          <button
            type="button"
            disabled={timeframeOffset <= 0}
            onClick={() => {
              triggerHaptic('light');
              setTimeframeOffset((prev) => Math.max(0, prev - 1));
            }}
            className={`p-1.5 rounded-lg bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-50 active:scale-95 cursor-pointer ${
              timeframeOffset <= 0 ? 'opacity-40 cursor-not-allowed' : ''
            }`}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Hero KPI Trend Card */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl p-4 border border-slate-200/80 dark:border-zinc-800 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                {activeSymptomMeta.shortName} Severity Average
              </div>
              <div className="text-2xl font-black text-slate-900 dark:text-white mt-0.5">
                {stats.avg}
              </div>
            </div>

            <div className="text-right">
              <span
                className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                  stats.trend === 'improving'
                    ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                    : stats.trend === 'elevating'
                    ? 'bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
                    : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400'
                }`}
              >
                {stats.trend === 'improving' ? (
                  <>
                    <TrendingDown className="w-3.5 h-3.5" />
                    <span>Downwards Trend</span>
                  </>
                ) : stats.trend === 'elevating' ? (
                  <>
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span>Upwards Trend</span>
                  </>
                ) : (
                  <span>Steady Baseline</span>
                )}
              </span>
              <div className="text-[10px] text-slate-400 dark:text-zinc-500 mt-1">
                {stats.positiveCount} flares / {stats.count} check-ins
              </div>
            </div>
          </div>

          {/* SVG Line of Best Fit Chart (0-10 Scale) */}
          <div className="relative bg-slate-50 dark:bg-zinc-950/60 rounded-2xl p-2 border border-slate-100 dark:border-zinc-800/80">
            {chartPoints.length === 0 ? (
              <div className="h-36 flex flex-col items-center justify-center text-xs text-slate-400 dark:text-zinc-500">
                <Activity className="w-6 h-6 mb-1 opacity-40" />
                <span>No logs recorded for this period.</span>
              </div>
            ) : (
              <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-40 overflow-visible">
                <defs>
                  <linearGradient id="trendGradient10" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Y-axis Guides for 0, 2, 5, 8, 10 */}
                {[0, 2, 5, 8, 10].map((val) => {
                  const y = paddingTop + plotHeight - (val / maxY) * plotHeight;
                  return (
                    <g key={val}>
                      <line
                        x1={paddingX}
                        y1={y}
                        x2={chartWidth - paddingX}
                        y2={y}
                        stroke="currentColor"
                        strokeDasharray="2,2"
                        className="text-slate-200 dark:text-zinc-800"
                        strokeWidth={1}
                      />
                      <text
                        x={paddingX - 4}
                        y={y + 3}
                        textAnchor="end"
                        className="text-[9px] font-bold fill-slate-400 dark:fill-zinc-600"
                      >
                        {val}
                      </text>
                    </g>
                  );
                })}

                {/* Line of Best Fit Soft Shading */}
                <polygon
                  points={`${bestFitLine.x1},${paddingTop + plotHeight} ${bestFitLine.x1},${bestFitLine.y1} ${bestFitLine.x2},${bestFitLine.y2} ${bestFitLine.x2},${paddingTop + plotHeight}`}
                  fill="url(#trendGradient10)"
                  opacity={0.3}
                />

                {/* Single Clean Line of Best Fit */}
                <line
                  x1={bestFitLine.x1}
                  y1={bestFitLine.y1}
                  x2={bestFitLine.x2}
                  y2={bestFitLine.y2}
                  stroke="#3B82F6"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                />

                {/* Discrete Data Dots: ALL check-ins plotted, including 0 (calm/clear) */}
                {showDataPoints &&
                  svgCoordinates.map((coord, idx) => {
                    const isSelected = selectedPointIndex === idx;
                    const val = coord.pt.val;
                    return (
                      <g
                        key={idx}
                        className="cursor-pointer"
                        onClick={() => {
                          triggerHaptic('light');
                          setSelectedPointIndex(isSelected ? null : idx);
                        }}
                      >
                        <circle
                          cx={coord.x}
                          cy={coord.y}
                          r={isSelected ? 6 : val > 0 ? 4.5 : 3.5}
                          fill={val >= 7 ? '#EF4444' : val >= 4 ? '#F59E0B' : val > 0 ? '#3B82F6' : '#10B981'}
                          stroke="#FFFFFF"
                          strokeWidth={isSelected ? 2.5 : 1.5}
                          className="transition-all"
                        />
                      </g>
                    );
                  })}
              </svg>
            )}

            {/* Selected Point Tooltip Banner */}
            {selectedPointIndex !== null && svgCoordinates[selectedPointIndex] && (
              <div className="mt-2 p-2.5 bg-white dark:bg-zinc-800 rounded-xl border border-slate-200 dark:border-zinc-700 text-xs shadow-xs animate-fade-in flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">
                    {svgCoordinates[selectedPointIndex].pt.dateLabel} at{' '}
                    {svgCoordinates[selectedPointIndex].pt.timeLabel}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                    {activeSymptomMeta.name}:{' '}
                    <span className="font-bold">
                      {svgCoordinates[selectedPointIndex].pt.val}/10{' '}
                      {svgCoordinates[selectedPointIndex].pt.val === 0 ? '(Calm / No Symptoms)' : ''}
                    </span>
                    {svgCoordinates[selectedPointIndex].pt.symptom.notes && (
                      <span> • {svgCoordinates[selectedPointIndex].pt.symptom.notes}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPointIndex(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Suspected Ingredients for Selected Symptom */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400 flex items-center space-x-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span>Suspected Ingredients ({activeSymptomMeta.shortName})</span>
            </h3>
            <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
              Likelihood ranking
            </span>
          </div>

          {suspectedTriggers.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 rounded-3xl p-5 text-center border border-slate-200/80 dark:border-zinc-800 text-xs text-slate-400 dark:text-zinc-500 space-y-1">
              <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-500 opacity-80 mb-1" />
              <p className="font-bold text-slate-700 dark:text-zinc-300">
                No elevated triggers detected
              </p>
              <p className="text-[11px]">
                Log meals and check-ins consistently to refine statistical triggers for {activeSymptomMeta.shortName}.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {suspectedTriggers.map((trig) => {
                const isExpanded = expandedIngredient === trig.ingredient;
                return (
                  <div
                    key={trig.ingredient}
                    className="bg-white dark:bg-zinc-900 rounded-2xl p-3.5 border border-slate-200/80 dark:border-zinc-800 shadow-2xs transition-all space-y-2"
                  >
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => {
                        triggerHaptic('light');
                        setExpandedIngredient(isExpanded ? null : trig.ingredient);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-black text-slate-900 dark:text-white">
                          {trig.ingredient}
                        </span>
                        <span
                          className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            trig.confidence === 'High'
                              ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-800'
                              : trig.confidence === 'Moderate'
                              ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800'
                              : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400'
                          }`}
                        >
                          {trig.confidence} Likelihood
                        </span>
                      </div>

                      <div className="flex items-center space-x-1.5">
                        <span className="text-xs font-black text-slate-900 dark:text-white">
                          {trig.likelihoodPercent}%
                        </span>
                        <ChevronRight
                          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                    </div>

                    {/* Progress / Likelihood Bar */}
                    <div className="w-full h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          trig.likelihoodPercent >= 70
                            ? 'bg-gradient-to-r from-amber-500 to-rose-500'
                            : trig.likelihoodPercent >= 45
                            ? 'bg-gradient-to-r from-blue-500 to-amber-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${trig.likelihoodPercent}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-zinc-500 font-bold">
                      <span>
                        Eaten {trig.timesEaten}x • Triggered {trig.reactionCount}x
                      </span>
                      <span>
                        Avg Lag: {trig.avgLagHours}h ({trig.lagLabel})
                      </span>
                    </div>

                    {/* Expanded Meal Exposures */}
                    {isExpanded && (
                      <div className="pt-2 border-t border-slate-100 dark:border-zinc-800/80 space-y-1.5 text-xs animate-fade-in">
                        <div className="text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                          Meals with {trig.ingredient}:
                        </div>
                        {meals
                          .filter((m) => {
                            const foods = extractFoodsFromMeal(m).map((f) => f.toLowerCase());
                            return foods.includes(trig.ingredient.toLowerCase());
                          })
                          .slice(0, 4)
                          .map((m) => (
                            <div
                              key={m.id}
                              className="p-2 rounded-xl bg-slate-50 dark:bg-zinc-800/60 flex items-center justify-between text-[11px]"
                            >
                              <div className="min-w-0 pr-2 truncate">
                                <span className="font-bold text-slate-800 dark:text-zinc-200">
                                  {m.description || m.meal_type}
                                </span>
                                <span className="text-[10px] text-slate-400 ml-1.5">
                                  {formatIsoDate(new Date(m.timestamp_iso))} {formatTime(m.timestamp_iso)}
                                </span>
                              </div>
                              {onEditMeal && (
                                <button
                                  type="button"
                                  onClick={() => onEditMeal(m)}
                                  className="text-blue-600 dark:text-blue-400 font-bold text-[10px] shrink-0 cursor-pointer"
                                >
                                  View
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-white dark:bg-zinc-900 rounded-3xl p-5 border border-slate-200 dark:border-zinc-800 shadow-xl space-y-4 animate-scale-in">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-zinc-800">
              <div className="flex items-center space-x-2">
                <Settings className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-black text-slate-900 dark:text-white">
                  Chart Configurations
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-400 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <label className="flex items-center justify-between p-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-800/60 cursor-pointer">
                <span className="font-bold text-slate-700 dark:text-zinc-300">
                  Show Non-Zero Symptom Flare Dots
                </span>
                <input
                  type="checkbox"
                  checked={showDataPoints}
                  onChange={(e) => setShowDataPoints(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600"
                />
              </label>
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => {
                  setShowConfigModal(false);
                  setIsCustomizeSymptomsOpen(true);
                }}
                className="w-full py-2.5 px-3 rounded-2xl border border-slate-200 dark:border-zinc-700 bg-slate-50 hover:bg-slate-100 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-200 font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <Sliders className="w-4 h-4 text-blue-500" />
                <span>Customize Symptom Sliders</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowConfigModal(false)}
              className="w-full py-2.5 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black font-black text-xs shadow-xs cursor-pointer"
            >
              Apply Settings
            </button>
          </div>
        </div>
      )}

      {/* Customize Symptoms Modal */}
      <CustomizeSymptomsModal
        isOpen={isCustomizeSymptomsOpen}
        onClose={() => {
          setIsCustomizeSymptomsOpen(false);
          setActiveSymptoms(getActiveTrackableSymptoms());
        }}
      />
    </div>
  );
};
